"use client";
import React from 'react';
import { motion } from 'framer-motion';

const industries = [
    'Startups building their market presence',
    'Growing businesses entering new markets',
    'Real Estate & Property Brands',
    'Healthcare & Wellness Businesses',
    'SaaS & Technology Companies',
    'Ecommerce & Consumer Brands',
    'Professional Service Firms',
];

/* ── Orbit nodes: angle (deg) + label + icon path ── */
const nodes = [
    {
        angle: -90, label: 'Startups',
        icon: <path d="M12 3l1.5 4h4l-3.5 2.5 1.5 4L12 11l-3.5 2.5 1.5-4L6.5 7h4z" stroke="#3730a3" strokeWidth="1.3" strokeLinejoin="round" fill="none"/>,
    },
    {
        angle: -30, label: 'SaaS & Tech',
        icon: <><path d="M7 10l-2-2 2-2" stroke="#3730a3" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><path d="M17 10l2-2-2-2" stroke="#3730a3" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 7l-4 6" stroke="#3730a3" strokeWidth="1.3" strokeLinecap="round"/></>,
    },
    {
        angle: 30, label: 'Ecommerce',
        icon: <><path d="M6 7h13l-1.5 6H7.5z" stroke="#3730a3" strokeWidth="1.3" strokeLinejoin="round" fill="none"/><circle cx="9" cy="15.5" r="1" fill="#3730a3"/><circle cx="14" cy="15.5" r="1" fill="#3730a3"/><path d="M4 5h2l1 2" stroke="#3730a3" strokeWidth="1.3" strokeLinecap="round"/></>,
    },
    {
        angle: 90, label: 'Professional',
        icon: <><rect x="7" y="8" width="10" height="8" rx="1" stroke="#3730a3" strokeWidth="1.3" fill="none"/><path d="M9 8V7a1 1 0 011-1h4a1 1 0 011 1v1" stroke="#3730a3" strokeWidth="1.3"/></>,
    },
    {
        angle: 150, label: 'Healthcare',
        icon: <><path d="M12 7v10M7 12h10" stroke="#3730a3" strokeWidth="1.6" strokeLinecap="round"/></>,
    },
    {
        angle: 210, label: 'Real Estate',
        icon: <><path d="M5 11l7-7 7 7v8H15v-5h-6v5H5z" stroke="#3730a3" strokeWidth="1.3" strokeLinejoin="round" fill="none"/></>,
    },
];

const ORBIT_R = 108;   // orbit radius
const CX = 160;        // centre x of SVG
const CY = 160;        // centre y of SVG
const NODE_R = 28;     // node circle radius

function toRad(deg: number) { return (deg * Math.PI) / 180; }
// Rounded to avoid server/client floating-point mismatches (hydration errors) in Math.cos/sin output.
function round(n: number) { return Math.round(n * 1000) / 1000; }

export default function BrandGoals() {
    return (
        <section
            className="relative overflow-hidden py-24 px-6 md:px-16 lg:px-24"
            style={{ background: '#ffffff', borderBottomLeftRadius: '80px', borderBottomRightRadius: '80px' }}
        >
            {/* Dot grid */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage: 'radial-gradient(circle, rgba(55,48,163,0.07) 1.5px, transparent 1.5px)',
                    backgroundSize: '32px 32px',
                    maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
                    WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
                }}
            />
            <div className="absolute top-0 right-0 pointer-events-none" style={{ width: 420, height: 420, background: 'radial-gradient(circle, rgba(55,48,163,0.05) 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />

            <div className="max-w-[1400px] mx-auto flex flex-col gap-16 relative z-10">

                {/* ── Top: heading left + illustration right ── */}
                <div className="flex flex-col md:flex-row items-center gap-10 md:gap-0">

                    {/* Left: text */}
                    <div className="flex flex-col gap-5 flex-1 max-w-xl">
                        <motion.span
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="text-xs font-bold tracking-[0.2em] uppercase"
                            style={{ color: '#3730a3', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                        >
                            Who We Work With
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7 }}
                            className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-none"
                            style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', color: '#080400' }}
                        >
                            Who We<br />Work With
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.15 }}
                            className="text-base md:text-lg"
                            style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(8,4,0,0.55)', letterSpacing: '0.02em' }}
                        >
                            We partner with businesses that want more than a logo refresh.
                        </motion.p>
                    </div>

                    {/* Right: orbit illustration */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.88 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="flex-1 flex items-center justify-center"
                        style={{ minWidth: 420 }}
                    >
                        <svg viewBox="0 0 320 320" width="460" height="460" style={{ overflow: 'visible' }}>
                            <defs>
                                <style>{`
                                    @keyframes orbitSpin {
                                        from { transform: rotate(0deg); }
                                        to   { transform: rotate(360deg); }
                                    }
                                    @keyframes counterSpin {
                                        from { transform: rotate(0deg); }
                                        to   { transform: rotate(-360deg); }
                                    }
                                    .orbit-ring {
                                        transform-origin: ${CX}px ${CY}px;
                                        animation: orbitSpin 28s linear infinite;
                                    }
                                    .counter-node {
                                        animation: counterSpin 28s linear infinite;
                                        transform-box: fill-box;
                                        transform-origin: center;
                                    }
                                `}</style>
                            </defs>

                            {/* ── Static background rings ── */}
                            <circle cx={CX} cy={CY} r={148} fill="none" stroke="rgba(55,48,163,0.04)" strokeWidth="1"/>
                            <circle cx={CX} cy={CY} r={56}  fill="none" stroke="rgba(55,48,163,0.07)" strokeWidth="1" strokeDasharray="3 5"/>

                            {/* ── Rotating orbit group ── */}
                            <g className="orbit-ring">
                                {/* Dashed orbit ring */}
                                <circle
                                    cx={CX} cy={CY} r={ORBIT_R}
                                    fill="none"
                                    stroke="rgba(55,48,163,0.18)"
                                    strokeWidth="1"
                                    strokeDasharray="5 7"
                                />

                                {/* Spokes + nodes */}
                                {nodes.map((node, i) => {
                                    const rad = toRad(node.angle);
                                    const nx = round(CX + ORBIT_R * Math.cos(rad));
                                    const ny = round(CY + ORBIT_R * Math.sin(rad));
                                    return (
                                        <g key={i}>
                                            {/* Spoke */}
                                            <line
                                                x1={CX} y1={CY} x2={nx} y2={ny}
                                                stroke="rgba(55,48,163,0.12)"
                                                strokeWidth="1"
                                                strokeDasharray="3 4"
                                            />
                                            {/* Node circle */}
                                            <circle
                                                cx={nx} cy={ny} r={NODE_R}
                                                fill="white"
                                                stroke="rgba(55,48,163,0.30)"
                                                strokeWidth="1.5"
                                                style={{ filter: 'drop-shadow(0 2px 10px rgba(55,48,163,0.12))' }}
                                            />
                                            {/* Counter-rotating group (icon + label stays upright) */}
                                            <g className="counter-node" style={{ transformOrigin: `${nx}px ${ny}px`, transformBox: 'view-box' } as React.CSSProperties}>
                                                {/* Icon centred in node */}
                                                <svg x={nx - 12} y={ny - 12} width="24" height="24" viewBox="0 0 24 24">
                                                    {node.icon}
                                                </svg>
                                                {/* Label */}
                                                <text
                                                    x={nx}
                                                    y={ny + NODE_R + 12}
                                                    textAnchor="middle"
                                                    fontSize="7.5"
                                                    fontWeight="700"
                                                    letterSpacing="0.08em"
                                                    fill="rgba(55,48,163,0.55)"
                                                    fontFamily="var(--font-clash), 'Clash Display', sans-serif"
                                                >
                                                    {node.label.toUpperCase()}
                                                </text>
                                            </g>
                                        </g>
                                    );
                                })}

                                {/* Corner accent dots on the outer ring */}
                                {[0, 60, 120, 180, 240, 300].map((deg, i) => {
                                    const r2 = toRad(deg);
                                    return (
                                        <circle
                                            key={i}
                                            cx={round(CX + 148 * Math.cos(r2))}
                                            cy={round(CY + 148 * Math.sin(r2))}
                                            r={2.5}
                                            fill="rgba(55,48,163,0.20)"
                                        />
                                    );
                                })}
                            </g>

                            {/* ── Centre hub (static, never rotates) ── */}
                            <circle cx={CX} cy={CY} r={44} fill="rgba(55,48,163,0.07)" stroke="rgba(55,48,163,0.20)" strokeWidth="1.5"/>
                            <circle cx={CX} cy={CY} r={30} fill="#3730a3"/>
                            <text
                                x={CX} y={CY + 5}
                                textAnchor="middle"
                                fontSize="13"
                                fontWeight="700"
                                fill="#ffffff"
                                fontFamily="var(--font-clash), 'Clash Display', sans-serif"
                                letterSpacing="0.06em"
                            >
                                LW
                            </text>
                        </svg>
                    </motion.div>
                </div>

                {/* ── Industry cards ── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {industries.map((industry, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 32, scale: 0.96 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.55, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                            whileHover={{
                                y: -3,
                                boxShadow: '0 0 0 1.5px #3730a3, 0 6px 32px rgba(55,48,163,0.20), 0 0 56px rgba(55,48,163,0.09)',
                                background: 'rgba(55,48,163,0.04)',
                            }}
                            className="flex items-center gap-3 p-5 rounded-2xl cursor-default"
                            style={{
                                background: '#ffffff',
                                border: '1.5px solid rgba(55,48,163,0.28)',
                                boxShadow: '0 2px 12px rgba(55,48,163,0.06)',
                                transition: 'background 0.25s ease',
                            }}
                        >
                            <span
                                className="shrink-0 flex items-center justify-center rounded-full"
                                style={{ width: 30, height: 30, background: 'rgba(55,48,163,0.08)', border: '1.5px solid rgba(55,48,163,0.22)' }}
                            >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 6l3 3 5-5" stroke="#3730a3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </span>
                            <span
                                className="text-sm font-medium"
                                style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(8,4,0,0.75)', letterSpacing: '0.02em' }}
                            >
                                {industry}
                            </span>
                        </motion.div>
                    ))}
                </div>

                {/* ── Closing line ── */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="flex flex-col gap-4"
                >
                    <div style={{ height: 1, background: 'linear-gradient(90deg, #3730a3 0%, transparent 100%)', opacity: 0.15 }} />
                    <p
                        className="text-lg md:text-xl"
                        style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(8,4,0,0.60)', letterSpacing: '0.04em', fontWeight: 400 }}
                    >
                        Whether you&apos;re launching, repositioning, or scaling, we help create a brand that aligns with how your audience thinks and buys.
                    </p>
                </motion.div>

            </div>
        </section>
    );
}
